import {
    AiFeedCategory,
    ContentBlockJSON,
    ContentBlockType,
    IChartSegment,
    IContentBlock,
    ICreateFeedTemplate,
    IFeedItem,
    IFeedTemplate,
    IFormBody,
    IUpdateFeedTemplate,
} from './types'

// ── style 토큰 (ContentBlock v2) ───────────────────────────────────────────────
// 크기(display/title/headline/body/caption)는 서로 배타적이고, 굵기(bold)·색(positive/negative/accent)·
// 장식(link)·마스킹(blur)·줄바꿈(block)은 자유 조합. 크기는 굵기를 함축하지 않는다(v2에서 bold는 항상 명시적).
export interface IStyleParts {
    color?: 'positive' | 'negative' | 'accent'
    bold?: boolean
    display?: boolean // 가장 큰 크기
    title?: boolean // 24px
    headline?: boolean // 18px
    body?: boolean // 본문(기본) 크기
    caption?: boolean // 12px
    blur?: boolean // 마스킹 대상(viewer.is_masked 시 ●로 치환)
    link?: boolean // 밑줄
    block?: boolean // 이 조각 앞에서 줄바꿈(기본은 이어붙임)
}

// 'title_bold_block' 같은 조합 문자열을 개별 요소로 분해
export const parseStyleToken = (token?: string): IStyleParts => {
    if (!token) return {}
    const segments = token.split('_')
    const parts: IStyleParts = {}
    if (segments.includes('positive')) parts.color = 'positive'
    if (segments.includes('negative')) parts.color = 'negative'
    if (segments.includes('accent')) parts.color = 'accent'
    if (segments.includes('bold')) parts.bold = true
    if (segments.includes('display')) parts.display = true
    if (segments.includes('title')) parts.title = true
    if (segments.includes('headline')) parts.headline = true
    if (segments.includes('body')) parts.body = true
    if (segments.includes('caption')) parts.caption = true
    if (segments.includes('blur')) parts.blur = true
    if (segments.includes('link')) parts.link = true
    if (segments.includes('block')) parts.block = true
    return parts
}

// 개별 요소를 조합 문자열로 합침. 아무 요소도 없으면 undefined(스타일 없음).
// 크기 프리셋은 서로 배타적 — display > title > headline > body > caption 우선순위로 하나만 방출한다.
// bold는 크기와 독립적으로 켜고 끈다.
export const buildStyleToken = (parts: IStyleParts): string | undefined => {
    const segments: string[] = []
    if (parts.display) segments.push('display')
    else if (parts.title) segments.push('title')
    else if (parts.headline) segments.push('headline')
    else if (parts.body) segments.push('body')
    else if (parts.caption) segments.push('caption')
    if (parts.bold) segments.push('bold')
    if (parts.color) segments.push(parts.color)
    if (parts.blur) segments.push('blur')
    if (parts.link) segments.push('link')
    if (parts.block) segments.push('block')
    return segments.length ? segments.join('_') : undefined
}

// style 토큰을 미리보기용 tailwind class로 변환.
// opts.noBlur: blur 필터 제외. opts.noSelect: blur 시 select-none 제외(입력창에서 텍스트 선택/편집 허용).
// opts.noPointer: link의 cursor-pointer 제외. opts.noBlock: 'block'(줄바꿈) display 클래스 제외(에디터 안에서는 인라인 유지).
export const styleTokenToClassName = (
    token?: string,
    opts?: {noBlur?: boolean; noSelect?: boolean; noPointer?: boolean; noBlock?: boolean},
): string => {
    if (!token) return ''
    const parts = parseStyleToken(token)
    const classes: string[] = []
    // 크기 프리셋(배타적): display 32px > title 24px > headline 18px > body 14px(기본) > caption 12px
    if (parts.display) classes.push('text-[32px]')
    else if (parts.title) classes.push('text-[24px]')
    else if (parts.headline) classes.push('text-[18px]')
    else if (parts.body) classes.push('text-[14px]')
    else if (parts.caption) classes.push('text-[12px]')
    if (parts.bold) classes.push('font-bold')
    if (parts.color === 'positive' || parts.color === 'accent') classes.push('text-primary')
    if (parts.color === 'negative') classes.push('text-font_red')
    if (parts.blur && !opts?.noBlur) {
        classes.push('blur-[3px]')
        if (!opts?.noSelect) classes.push('select-none')
    }
    if (parts.link) {
        classes.push('underline')
        if (!opts?.noPointer) classes.push('cursor-pointer')
    }
    if (parts.block && !opts?.noBlock) classes.push('block') // display:block → 새 줄로 떨어짐(줄바꿈)
    return classes.join(' ')
}

// paragraph/button/container 텍스트와 url에 들어간 {TOKEN} 변수명을 전부 수집한다 (중첩 blocks 포함).
// 계약(kinds+common_vars) 밖 변수를 저장 전에 걸러내기 위한 강제 검증용.
export const extractVariableTokens = (blocks: IContentBlock[]): string[] => {
    const found = new Set<string>()
    const collect = (value?: string) => {
        if (!value) return
        const matches = value.match(/\{([A-Za-z0-9_]+)\}/g)
        matches?.forEach(token => found.add(token.slice(1, -1)))
    }
    const walk = (list: IContentBlock[]) => {
        list.forEach(block => {
            collect(block.text)
            collect(block.url)
            if (block.blocks) walk(block.blocks)
        })
    }
    walk(blocks || [])
    return Array.from(found)
}

export const uid = (): string => Math.random().toString(36).slice(2, 10)

export const escapeHtml = (text: string): string =>
    text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

export const newParagraphBlock = (text = ''): IContentBlock => ({id: uid(), type: 'paragraph', text})

export const newSegment = (): IChartSegment => ({id: uid(), label: '', percent: 0})

// v2 블록 타입별 기본값. variant/background/direction/gap/align/justify/size 등은 필수가 아니므로
// 기본값을 강제로 넣지 않는다 — 사용자가 명시적으로 고른 값만 블록에 기록되어 JSON에 방출된다.
// (미설정 시 프론트가 기본값을 적용: container→column/neutral, chart→bar, tag→tertiary, icon→md)
export const newBlockByType = (type: ContentBlockType): IContentBlock => {
    switch (type) {
        case 'paragraph':
            return {id: uid(), type: 'paragraph', text: ''}
        case 'container':
            return {id: uid(), type: 'container', blocks: []}
        case 'chart':
            return {id: uid(), type: 'chart', segments: []}
        case 'tag':
            return {id: uid(), type: 'tag', text: ''}
        case 'icon':
            return {id: uid(), type: 'icon', icon_url: ''}
        case 'image':
            return {id: uid(), type: 'image', image: ''}
        case 'button':
            return {id: uid(), type: 'button', text: '', url: ''}
        case 'button_mo':
            return {id: uid(), type: 'button_mo', text: '', url: ''}
    }
}

// 편집용 로컬 id를 제거하고 명세서 그대로의 ContentBlock JSON 배열로 변환.
// 편집용 style 문자열('title_bold_block')을 body(JSON)용 배열(['title','bold','block'])로 변환한다.
// style은 필수값이 아니다 — 없으면 undefined(style 키 자체를 방출하지 않음).
const styleToArray = (style?: string): string[] | undefined => (style ? style.split('_') : undefined)

// body(JSON)의 style 배열(['title','bold','block'])을 편집용 문자열로 되돌린다. 빈 배열이면 undefined.
const styleFromJSON = (style?: string | string[]): string | undefined => {
    const segments = Array.isArray(style) ? style : style ? [style] : []
    return segments.length ? segments.join('_') : undefined
}

// 통화(currency) 변수 — 문단 텍스트에 이 토큰이 들어 있으면 그 조각만 별도 블록('기존타입_currency')으로 분리한다.
// constants.ts VARIABLE_DESCRIPTIONS에서 '(통화 변수)'로 표기된 것과 일치시킨다.
export const CURRENCY_VARIABLES = ['PROFIT_AMT', 'BALANCE'] as const

// '{PROFIT_AMT}' / '{BALANCE}' 리터럴 토큰. split용(캡처 그룹)과 단일 판별용을 함께 만든다.
const currencyTokenPatterns = CURRENCY_VARIABLES.map(name => `\\{${name}\\}`)
const CURRENCY_SPLIT_RE = new RegExp(`(${currencyTokenPatterns.join('|')})`)
const CURRENCY_TOKENS = new Set(CURRENCY_VARIABLES.map(name => `{${name}}`))

const hasCurrencyToken = (text?: string): boolean => !!text && CURRENCY_VARIABLES.some(name => text.includes(`{${name}}`))

// 통화 토큰이 섞인 paragraph 텍스트를 조각 단위로 쪼개 여러 블록으로 방출한다.
// - 통화 토큰 조각 → type이 '${type}_currency'
// - 나머지 텍스트 조각 → 원래 type 유지
// style은 모든 조각에 이어붙이되, 'block'(앞에서 줄바꿈)은 첫 조각에만 남겨 원래 한 줄 렌더를 보존한다.
const splitCurrencyParagraph = (block: IContentBlock): ContentBlockJSON[] => {
    const {id, blocks: children, segments, style, text, type, ...rest} = block
    const parts = parseStyleToken(style)
    const pieces = (text ?? '').split(CURRENCY_SPLIT_RE).filter(piece => piece !== '')
    return pieces.map((piece, index) => {
        // 두 번째 조각부터는 block(줄바꿈)을 떼서 앞 조각에 이어붙게 한다.
        const pieceStyle = buildStyleToken(index === 0 ? parts : {...parts, block: false})
        return {
            ...rest,
            type: CURRENCY_TOKENS.has(piece) ? `${type}_currency` : type,
            text: piece,
            ...(pieceStyle ? {style: styleToArray(pieceStyle)} : {}),
        }
    })
}

export const toContentBlockJSON = (blocks: IContentBlock[]): ContentBlockJSON[] => {
    return (blocks || []).flatMap((block): ContentBlockJSON[] => {
        // paragraph 텍스트에 통화 토큰이 있으면 조각 단위로 블록을 나눠 방출한다(통화 조각만 '기존타입_currency').
        if (block.type === 'paragraph' && hasCurrencyToken(block.text)) return splitCurrencyParagraph(block)

        // flex 등 단순 필드는 값이 있을 때만 rest로 방출된다(체크 해제 시 필드 자체가 없음).
        const {id, blocks: children, segments, style, ...rest} = block
        return [
            {
                ...rest,
                ...(style ? {style: styleToArray(style)} : {}),
                ...(children ? {blocks: toContentBlockJSON(children)} : {}),
                ...(segments
                    ? {
                          segments: segments.map(({id: segId, style: segStyle, ...seg}) => ({
                              ...seg,
                              ...(segStyle ? {style: styleToArray(segStyle)} : {}),
                          })),
                      }
                    : {}),
            },
        ]
    })
}

// JSON 직접 편집 화면에서 붙여넣은/수정한 JSON을 다시 편집용 IContentBlock[]로 변환 (로컬 id 새로 부여).
// paragraph 파생 타입(paragraph_currency 등)은 어드민 편집에서는 일반 paragraph로 다룬다.
export const fromContentBlockJSON = (json: any): IContentBlock[] => {
    if (!Array.isArray(json)) return []
    return json.map((block: any): IContentBlock => {
        const rawType: string = block?.type ?? 'paragraph'
        const type = (rawType.startsWith('paragraph') ? 'paragraph' : rawType) as ContentBlockType
        const parsed: IContentBlock = {id: uid(), type}
        if (block?.style !== undefined) parsed.style = styleFromJSON(block.style)
        if (block?.text !== undefined) parsed.text = block.text
        if (block?.image !== undefined) parsed.image = block.image
        if (block?.url !== undefined) parsed.url = block.url
        if (block?.icon_url !== undefined) parsed.icon_url = block.icon_url
        if (block?.size !== undefined) parsed.size = block.size
        if (block?.variant !== undefined) parsed.variant = block.variant
        if (block?.background !== undefined) parsed.background = block.background
        if (block?.direction !== undefined) parsed.direction = block.direction
        if (block?.align !== undefined) parsed.align = block.align
        if (block?.justify !== undefined) parsed.justify = block.justify
        if (block?.flex !== undefined) parsed.flex = block.flex
        if (block?.gap !== undefined) parsed.gap = block.gap
        if (Array.isArray(block?.blocks)) parsed.blocks = fromContentBlockJSON(block.blocks)
        if (Array.isArray(block?.segments)) {
            parsed.segments = block.segments.map((segment: any) => ({
                id: uid(),
                label: segment?.label ?? '',
                percent: Number(segment?.percent) || 0,
                style: styleFromJSON(segment?.style),
            }))
        }
        return parsed
    })
}

// ── 문서 템플릿 ↔ 어드민 편집 모델 매핑 ────────────────────────────────────────
// kind = 카테고리. blocks가 있으면 편집용 IContentBlock[]으로, 없으면 body 평문을 단일 문단으로 복원.
export const feedTemplateToItem = (template: IFeedTemplate): IFeedItem => ({
    id: template.id,
    post_type: 'auto_generated',
    category: template.kind as AiFeedCategory,
    active: template.active,
    created_at: template.created_at ? Math.floor(new Date(template.created_at).getTime() / 1000) : 0,
    content_blocks: template.blocks?.length
        ? fromContentBlockJSON(template.blocks)
        : template.body
        ? [newParagraphBlock(template.body)]
        : [],
})

// 폼 상태 → 백엔드로 방출할 blocks. JSON 직접편집 원본(raw_blocks)이 있으면 정규화 없이 그대로 방출하고,
// 없으면(UI 빌더 편집) content_blocks를 직렬화한다. 표시/전송 모두 이 함수를 단일 소스로 쓴다.
export const formBodyToBlocks = (body: IFormBody): ContentBlockJSON[] =>
    body.raw_blocks ?? toContentBlockJSON(body.content_blocks)

// 생성 폼 상태 → POST /v3/admin/feed_template 바디. content_blocks를 blocks(스켈레톤)로 방출하고 language는 현재 채널.
export const formBodyToCreateTemplate = (body: IFormBody, language: string): ICreateFeedTemplate => ({
    kind: body.category as string,
    language,
    blocks: formBodyToBlocks(body),
})

// 수정 폼 상태 → PUT /v3/admin/feed_template/:id 바디. kind/language는 불변이라 문구(blocks)만 보낸다.
export const formBodyToUpdateTemplate = (body: IFormBody): IUpdateFeedTemplate => ({
    blocks: formBodyToBlocks(body),
})
